/**
 * useMonthlyFinancials — Single source of truth for monthly financial data
 *
 * Composes:
 * - useEarned (three-pool server value: linked txn+verdict, sold verdict, ART_INCOME txn)
 * - useBurnProgress (real-time bank transactions for spend categories + sparkline)
 * - profile (burn target)
 *
 * Earned comes from the three-pool server computation, NOT from bank-only ART_INCOME.
 * This means sold verdicts appear in earned even before a bank deposit arrives.
 * Falls back to bank-only earned when the server value is stale (1st of month).
 */

import { useMemo } from 'react';
import { useBurnProgress } from '@/hooks/useBurnProgress';
import { useEarned } from '@/hooks/useEarned';
import { useAuth } from '@/contexts/AuthContext';
import type { Verdict } from '@/types/verdict';

export interface MonthlyFinancials {
  // Income
  artIncome: number;        // Three-pool earned (cents) — includes sold verdicts
  otherIncome: number;      // Non-art income if any (cents) — V1: 0
  totalIncome: number;      // artIncome + otherIncome

  // Spend by category (cents)
  suppliesCents: number;    // ART_SUPPLY
  businessCents: number;    // BUSINESS_EXPENSE
  personalCents: number;    // PERSONAL

  // Aggregated spend (cents)
  artSpend: number;         // suppliesCents + businessCents
  totalSpend: number;       // suppliesCents + businessCents + personalCents

  // Net (cents)
  artNet: number;           // artIncome - artSpend
  totalNet: number;         // totalIncome - totalSpend

  // Burn context (from profile, NOT from transactions)
  burnTarget: number;       // user.monthlyBurn from onboarding (cents)
  burnGap: number;          // burnTarget - totalIncome (positive = shortfall)
  burnCoveragePercent: number; // (totalIncome / burnTarget) * 100, capped at 100

  // Earned metadata
  earnedBreakdown: { pool1: number; pool2: number; pool3: number };
  earnedSource: 'server' | 'bank-fallback';

  // Month context
  dayOfMonth: number;
  daysInMonth: number;

  // Passthrough for sparkline and other consumers
  dailyCumulativeIncome: number[];
  dailyCumulativeEarned: number[];  // composite: bank + unlinked sold verdicts (cents)
  incomeDays: number[];
  progressPct: number;
  onTrack: boolean;
  isLoading: boolean;
}

export function useMonthlyFinancials(verdicts?: Verdict[]): MonthlyFinancials {
  const burn = useBurnProgress();
  const earned = useEarned();
  const { profile } = useAuth();

  const burnTarget = profile?.monthlyBurn ?? 0;

  const now = useMemo(() => new Date(), []);
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  // ── Composite daily earned: bank ART_INCOME + unlinked sold verdicts ──
  const dailyCumulativeEarned = useMemo(() => {
    const bankCumulative = burn.dailyCumulativeIncome;
    const buckets = new Array(daysInMonth).fill(0);

    for (let i = 0; i < bankCumulative.length && i < daysInMonth; i++) {
      buckets[i] = bankCumulative[i] - (i > 0 ? bankCumulative[i - 1] : 0);
    }

    if (verdicts) {
      const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      console.log('[useMonthlyFinancials] verdict merge', { monthKey, verdictCount: verdicts.length, soldVerdicts: verdicts.filter(v => v.sold).map(v => ({ id: v.id, sold: v.sold, soldDate: v.soldDate, soldPrice: v.soldPrice, linkedTransactionId: v.linkedTransactionId })) });
      for (const v of verdicts) {
        if (!v.sold || !v.soldDate || v.linkedTransactionId) continue;
        if (!v.soldDate.startsWith(monthKey)) continue;
        const day = parseInt(v.soldDate.slice(8, 10), 10);
        if (day < 1 || day > daysInMonth) continue;
        const price = v.soldPrice ?? 0;
        if (price > 0) buckets[day - 1] += price;
      }
    }

    const cumulative: number[] = [];
    let running = 0;
    for (let i = 0; i < daysInMonth; i++) {
      running += buckets[i];
      cumulative.push(running);
    }
    return cumulative;
  }, [burn.dailyCumulativeIncome, verdicts, daysInMonth, now]);

  // Three-pool earned (server-computed, includes sold verdicts without bank txns)
  // Falls back to bank-only when server value is stale (e.g. 1st of month before CF runs)
  const earnedSource: 'server' | 'bank-fallback' = earned.isStale ? 'bank-fallback' : 'server';
  const artIncome = earned.isStale ? burn.earnedCents : earned.earnedCents;
  const earnedBreakdown = earned.isStale
    ? { pool1: 0, pool2: 0, pool3: burn.earnedCents }
    : earned.breakdown;

  const otherIncome = 0; // V1: no non-art income tracking
  const totalIncome = artIncome + otherIncome;

  // Spend categories still come from bank transactions (useBurnProgress)
  const suppliesCents = burn.materialsCents;
  const businessCents = burn.overheadCents;
  const personalCents = burn.personalCents;

  const artSpend = suppliesCents + businessCents;
  const totalSpend = suppliesCents + businessCents + personalCents;

  const artNet = artIncome - artSpend;
  const totalNet = totalIncome - totalSpend;

  const burnGap = burnTarget - totalIncome;
  const burnCoveragePercent = burnTarget > 0
    ? Math.min(100, Math.round((totalIncome / burnTarget) * 100))
    : 0;

  return {
    artIncome,
    otherIncome,
    totalIncome,
    suppliesCents,
    businessCents,
    personalCents,
    artSpend,
    totalSpend,
    artNet,
    totalNet,
    burnTarget,
    burnGap,
    burnCoveragePercent,
    earnedBreakdown,
    earnedSource,
    dayOfMonth,
    daysInMonth,
    dailyCumulativeIncome: burn.dailyCumulativeIncome,
    dailyCumulativeEarned,
    incomeDays: burn.incomeDays,
    progressPct: burn.progressPct,
    onTrack: burn.onTrack,
    isLoading: burn.isLoading,
  };
}

export default useMonthlyFinancials;